import { forwardRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, Barcode, Star } from "@phosphor-icons/react";
import { fmtEUR } from "@/lib/api";
import { itemColors, categoryColors } from "@/lib/category-colors";

const ItemGrid = forwardRef(function ItemGrid(
    {
        items,
        favourites,
        filter,
        setFilter,
        activeCategory,
        setActiveCategory,
        onAdd,
        onOpenScanner,
        onSearchSubmit,
        onInputFocus,
        disabled,
    },
    ref
) {
    // Distinct categories from items
    const categories = useMemo(() => {
        const set = new Set();
        for (const it of items) if (it.category) set.add(it.category);
        return Array.from(set).sort();
    }, [items]);

    const filtered = useMemo(() => {
        // Always exclude paused items from POS
        const visibleItems = items.filter((it) => !it.paused);
        let list;
        if (activeCategory === "ALL") {
            list = visibleItems
                .filter((it) => it.screen_position && it.screen_position > 0)
                .sort((a, b) => (a.screen_position || 0) - (b.screen_position || 0))
                .slice(0, 20);
        } else if (activeCategory === "QUICK") {
            list = (favourites || []).filter((it) => !it.paused).slice(0, 20);
        } else {
            list = visibleItems
                .filter((it) => it.category === activeCategory)
                .sort((a, b) => a.description.localeCompare(b.description, "el"));
        }
        if (filter.trim()) {
            const q = filter.toLowerCase();
            list = list.filter(
                (it) =>
                    it.code.toLowerCase().includes(q) ||
                    it.description.toLowerCase().includes(q) ||
                    (it.barcode1 || "").includes(q) ||
                    (it.barcode2 || "").includes(q) ||
                    (it.barcode3 || "").includes(q)
            );
        }
        return list;
    }, [items, favourites, filter, activeCategory]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const q = filter.trim();
        if (!q || disabled) return;
        const exact = items.find(
            (it) =>
                !it.paused &&
                (it.code === q ||
                    it.barcode1 === q ||
                    it.barcode2 === q ||
                    it.barcode3 === q)
        );
        if (exact) {
            onAdd(exact);
            setFilter("");
            return;
        }
        if (onSearchSubmit) {
            const ok = await onSearchSubmit(q);
            if (ok) setFilter("");
        }
    };

    const tabs = [
        { key: "ALL", label: "ΘΕΣΕΙΣ 1-20" },
        { key: "QUICK", label: "ΑΓΑΠΗΜΕΝΑ", icon: Star },
        ...categories.map((c) => ({ key: c, label: c })),
    ];

    return (
        <div className="bg-white border-2 border-[#0A0A0A] flex flex-col h-full min-h-0 relative">
            {disabled && (
                <div
                    data-testid="items-disabled-overlay"
                    className="absolute inset-0 z-10 bg-white/85 backdrop-blur-[2px] flex items-center justify-center"
                >
                    <div className="text-center px-6">
                        <div className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A] mb-2">
                            ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ
                        </div>
                        <div className="text-sm text-[#5C5F66] font-medium">
                            Δεν επιτρέπεται προσθήκη ειδών χωρίς πελάτη
                        </div>
                    </div>
                </div>
            )}
            <form
                onSubmit={handleSubmit}
                className="px-3 py-3 border-b border-[#E2E4E9] flex items-center gap-2"
            >
                <div className="relative flex-1">
                    <MagnifyingGlass
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F66]"
                    />
                    <Input
                        ref={ref}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        onFocus={() => onInputFocus && onInputFocus("item")}
                        placeholder="Σάρωση / αναζήτηση είδους..."
                        data-testid="item-search-input"
                        disabled={disabled}
                        className="rounded-none h-11 pl-9 border-[#E2E4E9]"
                    />
                </div>
                <Button
                    type="button"
                    onClick={onOpenScanner}
                    data-testid="scan-item-button"
                    className="rounded-none h-11 w-11 px-0 bg-white border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    title="Σάρωση barcode είδους"
                >
                    <Barcode size={18} weight="bold" />
                </Button>
            </form>

            <div
                className="flex flex-wrap border-b border-[#E2E4E9] bg-[#FAFAFA]"
                data-testid="category-tabs"
            >
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = activeCategory === t.key;
                    const isCategory = t.key !== "ALL" && t.key !== "QUICK";
                    const palette = isCategory ? categoryColors(t.key) : null;
                    const style = active && palette
                        ? { backgroundColor: palette.bg, borderColor: palette.border, color: "#0A0A0A" }
                        : !active && palette
                          ? { backgroundColor: palette.bg, color: "#0A0A0A" }
                          : undefined;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActiveCategory(t.key)}
                            data-testid={`tab-${t.key}`}
                            style={style}
                            className={`shrink-0 px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] border-r border-b border-[#E2E4E9] flex items-center gap-1.5 transition-all ${
                                active && !palette
                                    ? "bg-[#0A0A0A] text-white"
                                    : !palette
                                      ? "bg-white text-[#5C5F66] hover:bg-[#F4F5F7]"
                                      : ""
                            } ${active && palette ? "ring-2 ring-offset-1 ring-[#0A0A0A]" : ""}`}
                        >
                            {Icon && <Icon size={12} weight="fill" />}
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                {filtered.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5C5F66]">
                        {activeCategory === "QUICK"
                            ? "Ο πελάτης δεν έχει αγοράσει ακόμη είδη."
                            : items.length === 0
                              ? "Δεν υπάρχουν είδη. Προσθέστε από ΕΙΔΗ."
                              : "Δεν βρέθηκαν είδη σε αυτή την κατηγορία."}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {filtered.map((it) => {
                            const colors = itemColors(it);
                            return (
                                <button
                                    key={it.id}
                                    onClick={() => onAdd(it)}
                                    data-testid={`add-item-${it.code}`}
                                    style={{
                                        backgroundColor: colors.bg,
                                        borderColor: colors.border,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = colors.bgHover;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = colors.bg;
                                    }}
                                    className="relative text-left border-2 p-3 transition-all duration-150 active:scale-[0.97] min-h-[100px] flex flex-col justify-between"
                                >
                                    {it.screen_position > 0 && (
                                        <div className="absolute top-1 right-1 bg-[#FFCC00] text-[#0A0A0A] text-[10px] font-black w-6 h-6 flex items-center justify-center rounded-full">
                                            {it.screen_position}
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-mono text-[10px] text-[#5C5F66]">
                                            {it.code}
                                        </div>
                                        <div className="font-bold text-sm text-[#0A0A0A] line-clamp-2 leading-tight mt-0.5">
                                            {it.description}
                                        </div>
                                    </div>
                                    <div className="flex items-end justify-between mt-2">
                                        <div className="text-[9px] text-[#5C5F66] uppercase tracking-wider">
                                            ΦΠΑ {it.vat_rate}%
                                        </div>
                                        <div className="font-heading font-black text-lg text-[#002FA7] leading-none">
                                            {fmtEUR(it.unit_price)}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
});

export default ItemGrid;
